import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Text from '../../Atoms/Text';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { OrdersScheduleReportModal } from '../../Compounds/OrdersScheduleReport/OrdersScheduleReportModal';
import { OrdersReport } from '../../Compounds/OrdersScheduleReport/ordersReport';
import { viewTypeOf } from '../../Compounds/CustomTableViews/viewKey';
import { ScheduleSetting, useScheduleSettings } from '../../../store/ScheduleSettings';
import { getCurrentPreferences, OrdersViewPreferences, PrefKey } from '../../../store/User';
import { EditReportForm } from './EditReportForm';

const ORDERS_TYPE = 'order';

// Only an entry known to hold tabs alone goes to the Orders dialog, which writes the entry back as those tabs.
const holdsTabsAlone = (viewKey: string) => {
  const stored = getCurrentPreferences().content[viewKey as PrefKey] as OrdersViewPreferences | undefined;
  return !!stored && stored.customView !== true;
};

type ReadState = 'reading' | 'read' | 'failed';

export interface EditReportDialogProps {
  reportId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: (id: string) => void;
}

/** Edits a report you own from the Reports page: in the scheduler, or a pre-views Orders report in its own dialog. */
export const EditReportDialog = ({ reportId, onClose, onChanged, onDeleted }: EditReportDialogProps) => {
  const { schedules, fetchSchedules } = useScheduleSettings();
  const [readState, setReadState] = useState<ReadState>('reading');
  const opening = useRef(0);
  const lastSchedule = useRef<ScheduleSetting | undefined>(undefined);
  const found = readState === 'read' ? schedules.find((candidate) => candidate.id === reportId) : undefined;
  if (found) lastSchedule.current = found;
  const schedule = reportId ? found : lastSchedule.current;
  // Decided once per opening, so preferences that arrive or change mid-edit cannot swap the dialog under the user.
  const route = useRef({ opening: -1, id: '', stageOnly: false });
  if (found && (route.current.opening !== opening.current || route.current.id !== found.id)) {
    const stageOnly = viewTypeOf(found.view_key) === ORDERS_TYPE && holdsTabsAlone(found.view_key);
    route.current = { opening: opening.current, id: found.id, stageOnly };
  }
  const isStageOnlyOrdersReport = !!schedule && route.current.stageOnly;

  useEffect(() => {
    opening.current += 1;
    if (!reportId) return;
    setReadState('reading');
    let current = true;
    void fetchSchedules().then((read) => {
      if (current) setReadState(read ? 'read' : 'failed');
    });
    return () => {
      current = false;
    };
    // fetchSchedules is recreated on every render, so the list is read again only when another report is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const forThisOpening = <Args extends unknown[]>(callback: (...args: Args) => void) => {
    const mine = opening.current;
    return (...args: Args) => {
      if (mine === opening.current) callback(...args);
    };
  };

  return (
    <>
      {isStageOnlyOrdersReport && (
        <OrdersScheduleReportModal
          open={!!reportId}
          existingSchedule={schedule as OrdersReport}
          onClose={() => {
            onChanged();
            onClose();
          }}
        />
      )}
      <Dialog
        open={!!reportId && !isStageOnlyOrdersReport}
        onClose={onClose}
        fullWidth
        data-testid="edit-report-dialog">
        <DialogTitle>Edit report</DialogTitle>
        <DialogContent>
          {readState === 'reading' || isStageOnlyOrdersReport ? (
            <ComponentSpinner />
          ) : schedule ? (
            <EditReportForm
              schedule={schedule}
              onClose={forThisOpening(onClose)}
              onChanged={onChanged}
              onDeleted={onDeleted}
            />
          ) : (
            <Text
              size="sm14"
              color="grey8">
              We couldn&apos;t load this report. Please try again.
            </Text>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
